/**
 * v3.15-1104 — CF360 Dormant Banner
 *
 * Persistent banner rendered at the top of the DealDashboard whenever
 * `client.cf360_state === "in_cf360"`. Per JOINT_IMPLEMENTATION_PLAN §2.3.
 *
 * Surfaces:
 *   - "Sent at" timestamp + sending operator
 *   - Days dormant (amber chip past long-open threshold)
 *   - Credit-concerns note (from the most recent transfer)
 *   - "Mark Returned from CF360" button for admin/director/manager only
 *
 * Per joint lifecycle §3.3, operators see a clear business-rule message
 * if they try a blocked action. The banner is the always-visible
 * counterpart that tells them WHY the deal is paused.
 */
import { useMemo } from 'react';
import { ShieldAlert, Clock, User as UserIcon } from 'lucide-react';
import { Button } from './ui/button';

const ADMIN_DIR_MGR_ROLES = new Set([
  'admin', 'ptc_admin', 'ptc_director', 'ptc_manager',
  'company_director', 'company_manager',
]);

const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.round((Date.now() - t) / 86400000);
  } catch {
    return null;
  }
};

export const CF360DormantBanner = ({
  client,
  currentUser,
  longOpenThresholdDays = 60,
  onMarkReturnedClick,
  pendingTransfer,
}) => {
  const days = useMemo(() => daysSince(client?.cf360_sent_at), [client?.cf360_sent_at]);
  if (!client || client.cf360_state !== 'in_cf360') return null;

  const overThreshold = days != null && days >= longOpenThresholdDays;
  const canOverride = ADMIN_DIR_MGR_ROLES.has(currentUser?.role || '');

  return (
    <div
      data-testid="cf360-dormant-banner"
      className="border-l-4 border-amber-500 bg-amber-50 text-amber-950 rounded-md p-4 mb-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm">
              This client is currently in CF360
            </span>
            {days != null && (
              <span
                data-testid="cf360-days-dormant"
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border ${
                  overThreshold
                    ? 'bg-amber-200 text-amber-900 border-amber-400'
                    : 'bg-amber-100 text-amber-800 border-amber-300'
                }`}
              >
                <Clock className="h-3 w-3" />
                {days} day{days === 1 ? '' : 's'} dormant
                {overThreshold ? ' (long-open)' : ''}
              </span>
            )}
          </div>
          <div className="text-xs text-amber-900/90 mt-1 space-y-0.5">
            {client.cf360_sent_at && (
              <div>
                <span className="font-medium">Sent at:</span>{' '}
                {client.cf360_sent_at.replace('T', ' ').slice(0, 19)} UTC
              </div>
            )}
            {client.cf360_sent_by_user_id && (
              <div className="flex items-center gap-1">
                <UserIcon className="h-3 w-3" />
                <span className="font-medium">Initiated by:</span>{' '}
                {client.cf360_sent_by_user_id}
              </div>
            )}
            {pendingTransfer?.credit_concerns && (
              <div>
                <span className="font-medium">Credit concerns:</span>{' '}
                <em className="not-italic text-amber-900">
                  {pendingTransfer.credit_concerns}
                </em>
              </div>
            )}
            <div className="pt-1 text-[11px] text-amber-800/80">
              Hub automations and funding workflows are paused while this
              client is with CF360. They resume immediately on return.
            </div>
          </div>
        </div>
        {canOverride && (
          <Button
            data-testid="cf360-mark-returned-button"
            variant="outline"
            size="sm"
            className="border-amber-500 text-amber-900 hover:bg-amber-100"
            onClick={onMarkReturnedClick}
          >
            Mark Returned from CF360
          </Button>
        )}
      </div>
    </div>
  );
};

export default CF360DormantBanner;
